import { Dialog as ChakraDialog, Portal } from '@chakra-ui/react';
import type { ReactNode } from 'react';

import contractIcon from '../../assets/images/contract_icon.png';

const SIZE_MAX_WIDTH: Record<string, string> = {
  xs: '444px',
  sm: '600px',
  md: '900px',
  lg: '1200px',
  xl: '1536px',
};

export interface BaseDialogProps {
  open: boolean;
  loading?: boolean;
  error?: boolean | object | null;
  cancel?: boolean;
  purpose: string;
  title: string;
  subtitle?: ReactNode;
  showContractIcon?: boolean;
  children?: ReactNode;
  alert?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  useFullWidth?: boolean;
  handleClose: () => void;
  fasttrackDialog?: boolean;
}

const HEADER_BASE: React.CSSProperties = {
  padding: 30,
  marginBottom: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 8,
};

const CLOSE_BTN: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  fontSize: 28,
  lineHeight: 1,
  padding: 4,
};

export const BaseDialog = ({
  open,
  loading = false,
  error = null,
  cancel = false,
  purpose,
  title,
  subtitle = '',
  showContractIcon = false,
  children = null,
  alert = false,
  size = 'sm',
  useFullWidth = false,
  handleClose,
  fasttrackDialog = false,
}: BaseDialogProps) => {
  const maxWidth = SIZE_MAX_WIDTH[size] ?? SIZE_MAX_WIDTH.sm;
  const headerStyle: React.CSSProperties = {
    ...HEADER_BASE,
    backgroundColor: alert ? 'var(--chakra-colors-red-muted)' : 'var(--chakra-colors-blue-subtle)',
    flexShrink: 0,
  };
  // Only the body scrolls; the header (and its close button) stays pinned.
  const SCROLL_BODY: React.CSSProperties = { flex: 1, overflowY: 'auto', minHeight: 0 };
  const contentStyle: React.CSSProperties = fasttrackDialog
    ? { ...SCROLL_BODY, padding: 45, paddingTop: 30 }
    : {
        ...SCROLL_BODY,
        padding: 20,
        paddingTop: 30,
        display: 'flex',
        justifyContent: 'space-around',
      };

  return (
    <ChakraDialog.Root
      open={open}
      onOpenChange={(e) => {
        if (!e.open) {
          handleClose();
        }
      }}
    >
      <Portal>
        <ChakraDialog.Backdrop />
        <ChakraDialog.Positioner>
          <ChakraDialog.Content
            maxWidth={maxWidth}
            width={useFullWidth ? '100%' : undefined}
            maxHeight="90vh"
            display="flex"
            flexDirection="column"
            overflow="hidden"
            aria-labelledby={`${purpose}-dialog-title`}
            aria-describedby={`${purpose}-dialog-description`}
          >
            <div style={headerStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                <div style={{ width: 24 }} />
                {showContractIcon && (
                  <img src={contractIcon} alt={`${purpose} icon`} style={{ width: 43 }} />
                )}
                <button type="button" aria-label="close" onClick={handleClose} style={CLOSE_BTN}>
                  ×
                </button>
              </div>
              <h2
                style={{ whiteSpace: 'pre-wrap', textAlign: 'center', margin: 0 }}
                id={`${purpose}-dialog-title`}
              >
                {title}
              </h2>
              {subtitle && (
                <p
                  style={{
                    whiteSpace: 'pre-wrap',
                    textAlign: 'center',
                    paddingTop: 15,
                    margin: 0,
                  }}
                >
                  {subtitle}
                </p>
              )}
            </div>
            <div
              style={{
                width: '100%',
                height: 4,
                background: loading ? 'var(--chakra-colors-border)' : 'transparent',
                flexShrink: 0,
              }}
            >
              {loading && <div style={{ height: '100%', width: '30%', background: '#1976d2' }} />}
            </div>
            {error && !cancel && (
              <div
                style={{
                  backgroundColor: 'var(--chakra-colors-red-muted)',
                  padding: 10,
                  textAlign: 'center',
                  flexShrink: 0,
                }}
              >
                <h2 style={{ margin: 0 }}>Error sending your request.</h2>
              </div>
            )}
            <div style={contentStyle} id={`${purpose}-dialog-description`}>
              {children}
            </div>
          </ChakraDialog.Content>
        </ChakraDialog.Positioner>
      </Portal>
    </ChakraDialog.Root>
  );
};
